'use client';

import type { Driver } from '@cc-shopping/contracts/cross-site/marketplace/types';
import { AnalyticsSkuViewed } from '@cc-shopping/platform-analytics/AnalyticsSkuViewed';
import { theme } from '@cc-shopping/ui/theme';
import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { CreditCardIcon } from './CreditCardIcon';
import { CTA_LABELS, DRIVER_LABELS } from './constants';
import DriverCard from './DriverCard';
import { GRID_WIDTH, GridColumns } from './GridColumns';
import { useCustomStickyElement } from './hooks';
import { ProductCard } from './ProductCard';
import { useCreditCardsMultiCompareContext } from './provider';
export type CreditCardsMultiCompareSectionClientProps = {
  driver?: Driver;
  title?: string;
  hideDriver?: boolean;
  hideCustomContent?: boolean;
  ctaType: string;
  renderedChildren: ReactNode;
};
const hasTooltip = (driver?: object): driver is {
  valueTooltip?: string;
} => {
  return driver && 'valueTooltip' in driver ? true : false;
};
export const CreditCardsMultiCompareSectionClient = ({
  title,
  driver,
  ctaType,
  hideCustomContent,
  hideDriver,
  renderedChildren
}: CreditCardsMultiCompareSectionClientProps) => {
  const targetRef = useRef<HTMLDivElement | null>(null);
  const columnsRef = useRef<HTMLDivElement | null>(null);
  const [maxCtaHeight, setMaxCtaHeight] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const {
    offers,
    containerRef,
    disableMonetization
  } = useCreditCardsMultiCompareContext();
  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    let rafId: number | null = null;
    rafId = requestAnimationFrame(() => {
      if (!containerRef?.current) return;
      const updateWidth = () => {
        if (containerRef.current) {
          const width = containerRef.current.offsetWidth;
          setContainerWidth(width);
        }
      };
      updateWidth();
      resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(containerRef.current);
    });
    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver?.disconnect();
    };
  }, []);
  const isApplyNow = ctaType === 'applyNow';
  useCustomStickyElement({
    containerRef,
    targetRef,
    enabled: isApplyNow,
    offsetBottom: 20,
    offsetTop: 85
  });
  return <Box ref={targetRef} sx={theme => ({
    padding: '0.5rem',
    ...(isApplyNow && {
      position: 'relative',
      zIndex: 1,
      '&[isStuck]': {
        backgroundColor: theme.palette.common.white,
        boxShadow: `0 4px 8px 0 rgba(100,102,106,0.1), 0 1px 4px 0 rgba(100,102,106,0.36)`
      }
    })
  })}>
      <Box sx={theme_0 => ({
      width: '100%',
      ...(isApplyNow ? {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        padding: '1rem 1rem 1rem 0',
        gap: '1rem',
        fontSize: '1.25rem',
        lineHeight: '1.3'
      } : {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '1rem',
        lineHeight: '1.5',
        color: theme_0.palette.blues.dark
      })
    })}>
        <Typography sx={{
        whiteSpace: 'nowrap',
        ...(!isApplyNow && {
          position: 'sticky',
          backgroundColor: theme.palette.common.white,
          left: 0,
          paddingRight: '1rem'
        })
      }} variant={isApplyNow ? 'textLargeBold' : 'textBold'} component="span">
          {title || (driver ? DRIVER_LABELS[driver] : null) || CTA_LABELS[ctaType]}
        </Typography>

        {isApplyNow && <CreditCardIcon />}

        {!isApplyNow && <hr style={{
        width: '100%',
        backgroundColor: theme.palette.blues.dark,
        height: '1px',
        border: 'none'
      }} />}
      </Box>

      {!hideDriver && <Box>
          <GridColumns columns={offers.length} ref={columnsRef}>
            {offers.map((offer, ix) => <Box key={offer.id} sx={{
          display: 'grid',
          height: '100%'
        }}>
                {ctaType ? <AnalyticsSkuViewed id={offer.id} impressionSection="cc_multi_card_comparison" impressionPosition={ix} monetizable={offer.monetizable} name={offer.displayName} productId={offer.product?.id} productName={offer.product?.name} institutionId={offer.institution?.id} institutionName={offer.institution?.name} offerId={offer.id} offerName={offer.displayName} productType="credit_cards" level="offer" source="marketplace" enabled={true} {...containerWidth !== 0 && (ix + 1) * GRID_WIDTH > containerWidth && {
            scrollableAncestor: containerRef?.current || undefined,
            direction: 'horizontal'
          }}>
                    <Box>
                      <ProductCard offer={offer} disableMonetization={disableMonetization} ctaType={ctaType} maxCtaHeight={maxCtaHeight} setMaxCtaHeight={setMaxCtaHeight} />
                    </Box>
                  </AnalyticsSkuViewed> : !driver ? null : <DriverCard driver={driver} bonusType={offer.signUpBonusType} driverDetails={offer[driver]?.value ?? undefined} tooltip={hasTooltip(offer[driver]) ? offer[driver].valueTooltip : undefined} displayTooltip={offer.details?.introOfferDetails?.type !== 'no rewards'} displayType={offer.details?.signUpBonusDetails?.signUpBonusDetailsAnnotation ? false : offer.details?.introOfferDetails?.type !== 'no rewards'} />}
              </Box>)}
          </GridColumns>
        </Box>}

      {!hideCustomContent && <Box>
          <GridColumns columns={offers.length}>{renderedChildren}</GridColumns>
        </Box>}
    </Box>;
};